import { useState } from 'react';
import type { ColDef, ICellRendererParams } from '../../../components/ui/table';
import CountryFlag from '../../../components/shared/CountryFlag';
import { AUDIT_EXPORT_COLUMNS, formatAuditDateTime } from '../../../lib/auditColumnDefs';
import {
  collectUniqueScalarValues,
} from '../../../components/shared/GridColumnFilterPanel';
import {
  CopyableGridText,
  EmailCopyCellRenderer,
  EntityActionsCellRenderer,
  GridCellWrap,
  NameAvatarCell,
  StatusBadgeCellRenderer,
} from '../../../components/shared/agGridCells';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import type { ExcelColumn } from '../../../utils/excelExport';
import type { MerchantColumnFilterKey, MerchantGridRow, MerchantRow } from '../models/types';

type MerchantFilterColumnConfig = {
  key: MerchantColumnFilterKey;
  label: string;
  type?: 'select' | 'dateRange';
  getOptions?: (rows: MerchantRow[]) => string[];
};

export const MERCHANT_FILTER_COLUMNS: MerchantFilterColumnConfig[] = [
  {
    key: 'name',
    label: 'Merchant',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.name || '—')),
  },
  {
    key: 'email',
    label: 'Email',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.email || '—')),
  },
  {
    key: 'country',
    label: 'Country',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.country || '—')),
  },
  {
    key: 'status',
    label: 'Status',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.status || 'Inactive')),
  },
  {
    key: 'website',
    label: 'Website',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.website || '—')),
  },
  {
    key: 'industry',
    label: 'Industry',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.industry || '—')),
  },
  {
    key: 'createdAt',
    label: 'Created At',
    type: 'dateRange',
  },
];

/** "url1 +2" style summary — the full list is available by clicking the cell (see
 * StorefrontUrlsCellRenderer below) when there's more than one. */
export function formatStorefrontUrls(urls: string[]): string {
  if (urls.length === 0) return '—';
  if (urls.length === 1) return urls[0];
  return `${urls[0]} +${urls.length - 1}`;
}

function StorefrontUrlsCellRenderer(params: ICellRendererParams<MerchantGridRow, string[]>) {
  const [open, setOpen] = useState(false);
  const urls = params.value ?? [];

  if (urls.length <= 1) {
    return (
      <GridCellWrap>
        <span className="text-muted-foreground truncate">{formatStorefrontUrls(urls)}</span>
      </GridCellWrap>
    );
  }

  return (
    <div className="flex h-full items-center" onClick={(e) => e.stopPropagation()}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-full truncate text-left text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            {formatStorefrontUrls(urls)}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-2">
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {urls.map((url, idx) => (
              <div key={idx} className="truncate text-sm text-muted-foreground" title={url}>
                {url}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export const MERCHANT_COLUMN_LABELS = Object.fromEntries(
  MERCHANT_FILTER_COLUMNS.map(({ key, label }) => [key, label]),
) as Record<MerchantColumnFilterKey, string>;

function columnLabel(key: MerchantColumnFilterKey) {
  return MERCHANT_COLUMN_LABELS[key];
}

/** Same columns and visual order as the merchants grid (Action excluded). Status is pinned right. */
export const MERCHANT_EXPORT_COLUMNS: ExcelColumn[] = [
  { header: 'Merchant', key: 'name' },
  { header: 'Email', key: 'email' },
  { header: 'Country', key: 'country' },
  { header: 'MIDs', key: 'mids' },
  { header: 'Website', key: 'website' },
  { header: 'URLs', key: 'storefrontUrlsDisplay' },
  { header: 'Industry', key: 'industry' },
  ...AUDIT_EXPORT_COLUMNS,
  { header: 'Status', key: 'status' },
];

export function buildMerchantColumnFilterFields(sourceRows: MerchantRow[]) {
  return MERCHANT_FILTER_COLUMNS.map(({ key, label, getOptions, type }) => ({
    key,
    label,
    type: type ?? 'select',
    options: type === 'dateRange' ? [] : (getOptions?.(sourceRows) ?? []),
  }));
}

export function createMerchantColumnDefs(): ColDef<MerchantGridRow>[] {
  return [
    {
      field: 'name',
      headerName: columnLabel('name'),
      flex: 2,
      minWidth: 240,
      cellRenderer: (params: ICellRendererParams<MerchantGridRow>) => {
        if (!params.data) return null;
        return (
          <NameAvatarCell
            name={params.data.name}
            accent={params.data.accent}
            muted
          />
        );
      },
    },
    {
      field: 'email',
      headerName: columnLabel('email'),
      flex: 1.5,
      minWidth: 200,
      cellRenderer: EmailCopyCellRenderer,
    },
    {
      field: 'country',
      headerName: columnLabel('country'),
      flex: 1,
      minWidth: 180,
      cellRenderer: (params: ICellRendererParams<MerchantGridRow>) => (
        <GridCellWrap>
          <span className="inline-flex items-center gap-2">
            <CountryFlag
              countryName={params.value}
              countryFlag={params.data?.countryFlag}
              countryId={params.data?.countryId}
            />
            <span className="text-muted-foreground">{params.value || '—'}</span>
          </span>
        </GridCellWrap>
      ),
    },
    {
      field: 'mids',
      headerName: 'MIDs',
      width: 90,
      filterValueGetter: (params) => String(params.data?.mids ?? 0),
      cellRenderer: (params: ICellRendererParams<MerchantGridRow>) => {
        const value = Number(params.value);
        const label = value > 0 ? value.toLocaleString() : '—';
        return (
          <GridCellWrap>
            <span className="text-muted-foreground">{label}</span>
          </GridCellWrap>
        );
      },
    },
    {
      field: 'status',
      headerName: columnLabel('status'),
      width: 108,
      cellRenderer: StatusBadgeCellRenderer,
      pinned:'right'
    },
    {
      field: 'website',
      headerName: columnLabel('website'),
      flex: 1.2,
      minWidth: 180,
      cellRenderer: (params: ICellRendererParams<MerchantGridRow, string>) => (
        <GridCellWrap>
          {params.value && params.value !== '—' ? (
            <CopyableGridText
              text={params.value}
              textClassName="text-muted-foreground"
              copyLabel="Copy website"
            />
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </GridCellWrap>
      ),
    },
    {
      field: 'storefrontUrls',
      headerName: 'URLs',
      flex: 1.2,
      minWidth: 180,
      sortable: false,
      filterValueGetter: (params) => (params.data?.storefrontUrls ?? []).join(' '),
      cellRenderer: StorefrontUrlsCellRenderer,
    },
    {
      field: 'industry',
      headerName: columnLabel('industry'),
      flex: 1,
      minWidth: 150,
      cellRenderer: (params: ICellRendererParams<MerchantGridRow, string>) => (
        <GridCellWrap>
          <span className="text-muted-foreground">{params.value || '—'}</span>
        </GridCellWrap>
      ),
    },
    {
      colId: 'createdAt',
      headerName: columnLabel('createdAt'),
      width: 180,
      cellClass: 'text-muted-foreground',
      valueGetter: (params) => params.data?.createdAt ?? null,
      valueFormatter: (params) => formatAuditDateTime(String(params.value ?? '')),
      filter: {
        component: 'agDateColumnFilter',
        doesFilterPass: () => true,
      },
      filterParams: {
        filterOptions: ['equals', 'inRange'],
        maxNumConditions: 1,
        buttons: ['clear', 'apply'],
        closeOnApply: true,
      },
    },
    {
      colId: 'updatedAt',
      headerName: 'Updated At',
      width: 180,
      cellClass: 'text-muted-foreground',
      sortable: true,
      filter: false,
      valueGetter: (params) => params.data?.updatedAt ?? null,
      valueFormatter: (params) => formatAuditDateTime(String(params.value ?? '')),
    },
    {
      headerName: 'Action',
      width: 90,
      minWidth: 90,
      maxWidth: 90,
      sortable: false,
      filter: false,
      cellRenderer: EntityActionsCellRenderer,
      pinned: 'right',
    },
  ];
}
